import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { ApiService, Referral, PaginatedReferrals, isSrReferral } from '../services/apiService';
import { TriageDetailModal } from '../components/TriageDetailModal';
import { TriageCategoryBadge } from '../components/TriageCategoryBadge';
import { Flex } from '../components/Flex';
import { ActionMenu } from '../components/ActionMenu';
import { Spinner } from '../components/Spinner';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { SourceBadge } from '../components/SourceBadge';
import FeatherIcon from 'feather-icons-react';

function isReceptionLoaded(referral: Referral): boolean {
  if (isSrReferral(referral)) {
    return !!(
      referral.clinical_notes ||
      referral.triage_category_final ||
      referral.triage_category_suggested
    );
  }
  return referral.status === 'completed';
}

export const AITriagingPage: React.FC = () => {
  // AXIS207: Get current user role from backend
  const { user: currentUser } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [pagination, setPagination] = useState<PaginatedReferrals['pagination'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingRowId, setLoadingRowId] = useState<string | null>(null);
  const [extractingTriageId, setExtractingTriageId] = useState<string | null>(null);
  const [processingTriageIds, setProcessingTriageIds] = useState<Set<string>>(new Set());
  const [approvingTriageId, setApprovingTriageId] = useState<string | null>(null);
  const [assigningToAdminId, setAssigningToAdminId] = useState<string | null>(null);  // AXIS209
  const [error, setError] = useState<string | null>(null);
  const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'triaged' | 'approved' | 'assigned_to_admin'>('triaged');
  const [tabCounts, setTabCounts] = useState<{
    triaged: number;
    approved: number;
    assigned_to_admin: number;
  }>({
    triaged: 0,
    approved: 0,
    assigned_to_admin: 0
  });
  const [sortBy, setSortBy] = useState<'completedDate' | 'triageStatus' | 'confidence' | 'receptionStatus' | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  // AXIS167: Reception status filter (Loaded/Pending/All)
  const [receptionFilter, setReceptionFilter] = useState<'all' | 'loaded' | 'pending'>('all');
  // AXIS228: Search state
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get('search') || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>(searchTerm);

  // AXIS165: Reprocess confirmation modal state
  const [showReprocessConfirm, setShowReprocessConfirm] = useState(false);
  const [pendingReprocessId, setPendingReprocessId] = useState<string | null>(null);
  const [pendingReprocessReferral, setPendingReprocessReferral] = useState<Referral | null>(null);

  // Delete confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [referralToDelete, setReferralToDelete] = useState<Referral | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Bulk selection and processing state
  const [selectedReferralIds, setSelectedReferralIds] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // AXIS228: Debounce search term (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // AXIS228: Update URL when search term changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (debouncedSearchTerm) {
      newSearchParams.set('search', debouncedSearchTerm);
    } else {
      newSearchParams.delete('search');
    }
    if (newSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [debouncedSearchTerm]);

  // AXIS228: Reset to page 1 when search term changes
  useEffect(() => {
    if (debouncedSearchTerm !== searchParams.get('search')) {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    fetchReferrals();
    // Clear selection when changing tabs or pages
    setSelectedReferralIds(new Set());
  }, [currentPage, pageSize, activeTab, sortBy, sortOrder, receptionFilter, debouncedSearchTerm]);

  const fetchReferrals = async () => {
    try {
      setLoading(true);
      setError(null);
      // AXIS228: Pass search parameter along with triageTab
      const data = await ApiService.getReferrals(
        currentPage, 
        pageSize, 
        'completed', 
        debouncedSearchTerm || undefined, 
        undefined, 
        activeTab
      );
      
      // AXIS167: Apply reception filter client-side before sorting
      let filteredReferrals = [...data.referrals];
      if (receptionFilter !== 'all') {
        filteredReferrals = filteredReferrals.filter(referral => {
          const isLoaded = isReceptionLoaded(referral);
          return receptionFilter === 'loaded' ? isLoaded : !isLoaded;
        });
      }

      // Apply client-side sorting if sortBy is set
      let sortedReferrals = [...filteredReferrals];
      if (sortBy) {
        sortedReferrals.sort((a, b) => {
          let compareValue = 0;
          
          if (sortBy === 'completedDate') {
            // Sort by updated_at (completed date)
            const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
            const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
            compareValue = dateA - dateB;
          } else if (sortBy === 'triageStatus') {
            // Sort by triage category (1-4), with null/undefined at the end
            const categoryA = a.triage_category_final || a.triage_category_suggested || 999;
            const categoryB = b.triage_category_final || b.triage_category_suggested || 999;
            compareValue = categoryA - categoryB;
          } else if (sortBy === 'confidence') {
            // Sort by confidence percentage, with null/undefined at the end
            const confidenceA = a.triage_data?.confidence_percentage ?? -1;
            const confidenceB = b.triage_data?.confidence_percentage ?? -1;
            compareValue = confidenceA - confidenceB;
          } else if (sortBy === 'receptionStatus') {
            const statusA = isReceptionLoaded(a) ? 1 : 0;
            const statusB = isReceptionLoaded(b) ? 1 : 0;
            compareValue = statusA - statusB;
          }
          
          return sortOrder === 'asc' ? compareValue : -compareValue;
        });
      }
      
      setReferrals(sortedReferrals);
      setPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch referrals');
      console.error('Error fetching referrals:', err);
      setReferrals([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch tab counts for all tabs
  const fetchTabCounts = async () => {
    try {
      const [triagedData, approvedData, assignedToAdminData] = await Promise.all([
        ApiService.getReferrals(1, 1, 'completed', undefined, undefined, 'triaged'),
        ApiService.getReferrals(1, 1, 'completed', undefined, undefined, 'approved'),
        ApiService.getReferrals(1, 1, 'completed', undefined, undefined, 'assigned_to_admin')
      ]);
      
      setTabCounts({
        triaged: triagedData.pagination.total_count,
        approved: approvedData.pagination.total_count,
        assigned_to_admin: assignedToAdminData.pagination.total_count
      });
    } catch (err) {
      console.error('Error fetching tab counts:', err);
      setTabCounts({ triaged: 0, approved: 0, assigned_to_admin: 0 });
    }
  };

  // Fetch tab counts on mount and after referral updates
  useEffect(() => {
    fetchTabCounts();
  }, []);

  const handleViewDetail = async (referralId: string) => {
    try {
      const cachedReferral = referrals.find(r => r.id === referralId);

      if (cachedReferral) {
        // Open modal immediately with cached data
        setSelectedReferral(cachedReferral);
        setShowDetailModal(true);
        
        // Fetch full details in the background (for attachments, extracted fields, triage_data, etc.)
        (async () => {
          try {
            const fullReferral = await ApiService.getReferralById(referralId);
            setSelectedReferral(prev => {
              if (prev?.id === referralId) {
                return fullReferral;
              }
              return prev;
            });
          } catch (err) {
            console.error('Error fetching full referral details:', err);
          }
        })();
      } else {
        // Referral not in list, fetch it (shouldn't happen normally)
        setLoadingRowId(referralId);
        const referral = await ApiService.getReferralById(referralId);
        setSelectedReferral(referral);
        setShowDetailModal(true);
        setLoadingRowId(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch referral details');
      console.error('Error fetching referral details:', err);
      setLoadingRowId(null);
    }
  };

  const handleReferralUpdate = (updatedReferral: Referral) => {
    setSelectedReferral(updatedReferral);
    setReferrals(prevReferrals => 
      prevReferrals.map(r => r.id === updatedReferral.id ? updatedReferral : r)
    );
    // Refresh both referrals and tab counts after update
    fetchReferrals();
    fetchTabCounts();
  };

  const handleExtractTriage = async (referralId: string) => {
    try {
      setError(null);

      // AXIS165: Check if referral has overridden fields
      const referral = referrals.find(r => r.id === referralId);
      const hasOverrides = referral?.overridden_triage_fields && referral.overridden_triage_fields.length > 0;

      // If has overrides and this is a reprocess (not first extract), show confirmation
      const isReprocess = referral?.triage_data || referral?.triage_category_suggested;
      if (hasOverrides && isReprocess) {
        setPendingReprocessId(referralId);
        setPendingReprocessReferral(referral);
        setShowReprocessConfirm(true);
        return;
      }

      // Otherwise proceed with extraction
      await executeExtractTriage(referralId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to extract triage data';
      setError(errorMessage);
      console.error('Error extracting triage:', err);
    }
  };

  // AXIS165: Execute the actual triage extraction
  const executeExtractTriage = async (referralId: string, isBulk: boolean = false) => {
    try {
      if (!isBulk) {
        setExtractingTriageId(referralId);
      }
      setProcessingTriageIds(prev => new Set(prev).add(referralId));

      const result = await ApiService.extractTriage(referralId);

      if (result.success) {
        // AXIS161: Update the selected referral's triage data if this is the currently selected one
        if (selectedReferral && selectedReferral.id === referralId && result.triage_data) {
          setSelectedReferral({
            ...selectedReferral,
            triage_data: result.triage_data,
            triage_category_suggested: result.triage_data.triage_category_suggested,
            triage_category_final: result.triage_data.triage_category_suggested,
            // AXIS165: Clear overridden fields after reprocess
            overridden_triage_fields: []
          });
        }

        // Only refresh for single extractions, bulk will refresh at the end
        if (!isBulk) {
          await fetchReferrals();
          await fetchTabCounts();
        }
      } else {
        setError(result.message || 'Failed to extract triage data');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to extract triage data';
      setError(errorMessage);
      console.error('Error extracting triage:', err);
    } finally {
      if (!isBulk) {
        setExtractingTriageId(null);
      }
      setProcessingTriageIds(prev => {
        const next = new Set(prev);
        next.delete(referralId);
        return next;
      });
    }
  };

  // AXIS165: Handle reprocess confirmation
  const handleConfirmReprocess = async () => {
    if (pendingReprocessId) {
      setShowReprocessConfirm(false);
      await executeExtractTriage(pendingReprocessId);
      setPendingReprocessId(null);
      setPendingReprocessReferral(null);
    }
  };

  // AXIS165: Cancel reprocess
  const handleCancelReprocess = () => {
    setShowReprocessConfirm(false);
    setPendingReprocessId(null);
    setPendingReprocessReferral(null);
  };

  // Bulk selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(referrals.map(r => r.id));
      setSelectedReferralIds(allIds);
    } else {
      setSelectedReferralIds(new Set());
    }
  };

  const handleSelectRow = (referralId: string, checked: boolean) => {
    const newSelection = new Set(selectedReferralIds);
    if (checked) {
      newSelection.add(referralId);
    } else {
      newSelection.delete(referralId);
    }
    setSelectedReferralIds(newSelection);
  };

  // Bulk reprocess handler
  const handleBulkReprocess = async () => {
    if (selectedReferralIds.size === 0) return;

    try {
      setBulkProcessing(true);
      setError(null);
      const idsToProcess = Array.from(selectedReferralIds);

      // Process all referrals concurrently
      const results = await Promise.allSettled(
        idsToProcess.map(referralId => executeExtractTriage(referralId, true))
      );

      // Count successful and failed operations
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      // Clear selection and refresh
      setSelectedReferralIds(new Set());
      await fetchReferrals();
      await fetchTabCounts();

      if (failed > 0) {
        setError(`Reprocessed ${succeeded} of ${idsToProcess.length} referrals. ${failed} failed.`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Bulk reprocess failed';
      setError(errorMessage);
      console.error('Error bulk reprocessing:', err);
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleApproveTriage = async (referralId: string) => {
    try {
      setApprovingTriageId(referralId);
      setError(null);
      
      // Find the referral to check if triage_category_final is set
      const referral = referrals.find(r => r.id === referralId);
      if (!referral) {
        throw new Error('Referral not found');
      }
      
      // If triage_category_final is not set but we have a suggested category, save it first
      if (!referral.triage_category_final && referral.triage_category_suggested) {
        await ApiService.updateTriageCategory(referralId, referral.triage_category_suggested);
      }
      
      // Now approve the triage
      await ApiService.approveTriage(referralId);
      
      // Refresh the list and tab counts after successful approval
      await fetchReferrals();
      await fetchTabCounts();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to approve triage';
      setError(errorMessage);
      console.error('Error approving triage:', err);
    } finally {
      setApprovingTriageId(null);
    }
  };

  // AXIS209: Handle assign to admin
  const handleAssignToAdmin = async (referralId: string) => {
    try {
      setAssigningToAdminId(referralId);
      setError(null);
      
      await ApiService.assignToAdmin(referralId);
      
      // Refresh the list and tab counts after successful assignment
      await fetchReferrals();
      await fetchTabCounts();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to assign referral to admin';
      setError(errorMessage);
      console.error('Error assigning to admin:', err);
    } finally {
      setAssigningToAdminId(null);
    }
  };

  const handleTabChange = (tab: 'triaged' | 'approved' | 'assigned_to_admin') => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset pagination when switching tabs
    // AXIS167: Reset reception filter when switching tabs
    setReceptionFilter('all');
    // AXIS228: Search term persists when switching tabs (as per requirement)
  };

  const handleDelete = (referral: Referral) => {
    setReferralToDelete(referral);
    setShowDeleteConfirm(true);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!referralToDelete) return;
    
    setDeleting(true);
    setDeleteError(null);
    
    try {
      await ApiService.deleteReferral(referralToDelete.id);
      
      // If the deleted referral is currently open in the modal, close it
      if (selectedReferral?.id === referralToDelete.id) {
        setShowDetailModal(false);
        setSelectedReferral(null);
      }
      
      // Refresh the list and tab counts
      await Promise.all([fetchReferrals(), fetchTabCounts()]);
      setShowDeleteConfirm(false);
      setReferralToDelete(null);
      setError(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete referral');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const hasTriageData = (referral: Referral) => {
    return !!(referral.triage_data || referral.triage_category_suggested);
  };

  // Handle column header click for sorting
  // AXIS167: Added receptionStatus to sortable columns
  const handleSort = (column: 'completedDate' | 'triageStatus' | 'confidence' | 'receptionStatus') => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Render sortable column header
  // AXIS167: Added receptionStatus to sortable columns
  const renderSortableHeader = (label: string, column: 'completedDate' | 'triageStatus' | 'confidence' | 'receptionStatus') => {
    const isActive = sortBy === column;

    return (
      <th
        onClick={(e) => {
          e.stopPropagation(); // Prevent row click
          handleSort(column);
        }}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          width: '8rem'
        }}
        title={`Sort by ${label}`}
      >
        <Flex align="center" gap={4}>
          {label}
          <FeatherIcon
            icon={isActive ? (sortOrder === 'asc' ? "chevron-up" : "chevron-down") : "chevron-down"}
            size={16}
            style={{ opacity: isActive ? 1 : 0.2 }}
          />
        </Flex>
      </th>
    );
  };

  return (
    <Layout>
      <div className="ai-triaging-page">
        <div className="ai-triaging-card" style={{
          background: 'white',
          borderRadius: '8px',
          padding: '1.5rem',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div className="page-header">
            <h3>AI Triaging</h3>
            {/* <p className="page-description">
              View completed referrals and trigger triage extraction to categorize referrals by clinical urgency.
            </p> */}
          </div>

          {error && (
            <div className="error-message" style={{ padding: '1rem', background: '#fee', color: '#c33', borderRadius: '4px', marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          {/* AXIS228: Search input */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ position: 'relative', maxWidth: '400px' }}>
              <FeatherIcon
                icon="search"
                size={18}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#999',
                  pointerEvents: 'none'
                }}
              />
              <input
                type="text"
                placeholder="Search by patient name, NHI, or ACC number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem 0.5rem 2.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#E84B37';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#ddd';
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999'
                  }}
                  title="Clear search"
                >
                  <FeatherIcon icon="x" size={16} />
                </button>
              )}
            </div>
          </div>

          {/* AXIS107: Add tabs for Triaged and Approved */}
          <div className="tab-buttons" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #e0e0e0' }}>
            <button
              className={`tab ${activeTab === 'triaged' ? 'active' : ''}`}
              onClick={() => handleTabChange('triaged')}
              style={{
                background: 'none',
                border: 'none',
                padding: '0.75rem 1.5rem',
                cursor: 'pointer',
                borderBottom: activeTab === 'triaged' ? '2px solid #E84B37' : '2px solid transparent',
                color: activeTab === 'triaged' ? '#E84B37' : '#666',
                fontWeight: activeTab === 'triaged' ? 500 : 'normal'
              }}
            >
              Triaged ({tabCounts.triaged})
            </button>
            <button
              className={`tab ${activeTab === 'approved' ? 'active' : ''}`}
              onClick={() => handleTabChange('approved')}
              style={{
                background: 'none',
                border: 'none',
                padding: '0.75rem 1.5rem',
                cursor: 'pointer',
                borderBottom: activeTab === 'approved' ? '2px solid #E84B37' : '2px solid transparent',
                color: activeTab === 'approved' ? '#E84B37' : '#666',
                fontWeight: activeTab === 'approved' ? 500 : 'normal'
              }}
            >
              Approved ({tabCounts.approved})
            </button>

            {/* AXIS209: Assigned to Admin tab - visible to Admin and Manager only */}
            {currentUser?.role && ['admin', 'manager'].includes(currentUser.role) && (
              <button
                className={`tab ${activeTab === 'assigned_to_admin' ? 'active' : ''}`}
                onClick={() => handleTabChange('assigned_to_admin')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  borderBottom: activeTab === 'assigned_to_admin' ? '2px solid #E84B37' : '2px solid transparent',
                  color: activeTab === 'assigned_to_admin' ? '#E84B37' : '#666',
                  fontWeight: activeTab === 'assigned_to_admin' ? 500 : 'normal'
                }}
              >
                Assigned to Admin ({tabCounts.assigned_to_admin})
              </button>
            )}

            {/* AXIS167: Reception status filter */}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label htmlFor="reception-filter" style={{ fontSize: '0.875rem', color: '#666' }}>
                Reception:
              </label>
              <select
                id="reception-filter"
                value={receptionFilter}
                onChange={(e) => setReceptionFilter(e.target.value as 'all' | 'loaded' | 'pending')}
                style={{
                  padding: '0.375rem 0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All</option>
                <option value="loaded">Loaded</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>

          {/* Bulk action bar */}
          {selectedReferralIds.size > 0 && (
            <div style={{
              background: '#f0f7ff',
              border: '1px solid #b3d9ff',
              borderRadius: '4px',
              padding: '0.75rem 1rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{ fontWeight: 500, color: '#333' }}>
                  {selectedReferralIds.size} referral{selectedReferralIds.size !== 1 ? 's' : ''} selected
                </span>
                {bulkProcessing && (
                  <span style={{ color: '#666', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Spinner size="sm" />
                    Processing {selectedReferralIds.size} referral{selectedReferralIds.size !== 1 ? 's' : ''}...
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={handleBulkReprocess}
                  disabled={bulkProcessing}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  {bulkProcessing ? (
                    <>
                      <Spinner size="sm" />
                      Reprocessing...
                    </>
                  ) : (
                    <>
                      <FeatherIcon icon="refresh-cw" size={16} />
                      Reprocess/Extract Selected
                    </>
                  )}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setSelectedReferralIds(new Set())}
                  disabled={bulkProcessing}
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}

          <div className="referrals-table-container">
            <table className="referrals-table">
              <thead>
                <tr>
                  {activeTab === 'assigned_to_admin' ? (
                    // AXIS209: Assigned to Admin tab columns
                    <>
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={referrals.length > 0 && selectedReferralIds.size === referrals.length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          disabled={bulkProcessing}
                        />
                      </th>
                      <th>Patient</th>
                      <th>Source</th>
                      <th>NHI</th>
                      <th>Assigned Date</th>
                      <th>Assigned By</th>
                      <th>Reception Status</th>
                      <th style={{ width: '120px' }}>Action</th>
                    </>
                  ) : (
                    // Standard tabs columns (Triaged, Approved)
                    <>
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={referrals.length > 0 && selectedReferralIds.size === referrals.length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          disabled={bulkProcessing}
                        />
                      </th>
                      {renderSortableHeader('Completed', 'completedDate')}
                      <th>Patient</th>
                      <th>Source</th>
                      <th>Date of Injury</th>
                      <th>Referral Type</th>
                      {renderSortableHeader('Priority', 'triageStatus')}
                      {renderSortableHeader('Confidence', 'confidence')}
                      {renderSortableHeader('Reception', 'receptionStatus')}
                      <th style={{ width: '120px' }}>Action</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={activeTab === 'assigned_to_admin' ? 8 : 10} style={{ textAlign: 'center', padding: '20px' }}>
                      Loading referrals...
                    </td>
                  </tr>
                ) : error && referrals.length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === 'assigned_to_admin' ? 8 : 10} style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
                      Error: {error}
                    </td>
                  </tr>
                ) : referrals.length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === 'assigned_to_admin' ? 8 : 10} style={{ textAlign: 'center', padding: '20px' }}>
                      No {activeTab === 'assigned_to_admin' ? 'referrals assigned to admin' : 'completed referrals'} found
                    </td>
                  </tr>
                ) : (
                  referrals.map(referral => {
                    const isProcessing = processingTriageIds.has(referral.id);
                    return (
                    <tr 
                      key={referral.id}
                      onClick={(e) => {
                        // Don't trigger row click if clicking checkbox or if processing
                        if ((e.target as HTMLElement).tagName !== 'INPUT' && !isProcessing) {
                          handleViewDetail(referral.id);
                        }
                      }}
                      style={{ 
                        cursor: isProcessing ? 'default' : 'pointer',
                        backgroundColor: isProcessing ? '#f0f7ff' : undefined,
                        transition: 'background-color 0.2s'
                      }}
                    >
                      {activeTab === 'assigned_to_admin' ? (
                        // AXIS209: Assigned to Admin tab row
                        <>
                          <td onClick={(e) => e.stopPropagation()}>
                            {isProcessing ? (
                              <Spinner size="sm" color="secondary" />
                            ) : (
                              <input
                                type="checkbox"
                                checked={selectedReferralIds.has(referral.id)}
                                onChange={(e) => handleSelectRow(referral.id, e.target.checked)}
                                disabled={bulkProcessing}
                              />
                            )}
                          </td>
                          <td>
                            <span 
                              className="patient-link"
                              style={{ color: '#E84B37' }}
                            >
                              {referral.patient_first_name} {referral.patient_last_name}
                            </span>
                            {referral.recommended_clinic_name && (
                              <div style={{ marginTop: '3px' }}>
                                <span style={{
                                  display: 'inline-block',
                                  padding: '1px 7px',
                                  borderRadius: '10px',
                                  fontSize: '0.72rem',
                                  background: (referral.clinic_recommendation_confidence ?? 100) >= 70 ? '#d1fae5' : (referral.clinic_recommendation_confidence ?? 100) >= 40 ? '#fef3c7' : '#fee2e2',
                                  color: (referral.clinic_recommendation_confidence ?? 100) >= 70 ? '#065f46' : (referral.clinic_recommendation_confidence ?? 100) >= 40 ? '#92400e' : '#991b1b',
                                }}>
                                  {referral.recommended_clinic_name}
                                </span>
                              </div>
                            )}
                          </td>
                          <td>
                            <SourceBadge source={referral.source} />
                          </td>
                          <td>
                            {referral.nhi_number || 'N/A'}
                          </td>
                          <td>
                            {formatDate(referral.assigned_to_admin_at)}
                          </td>
                          <td>
                            {/* Assigned by user - we'll fetch this from referral data */}
                            {referral.assigned_to_admin_by_user_id ? 'Staff' : 'N/A'}
                          </td>
                          {/* AXIS209: Reception status indicator column */}
                          <td>
                            {isReceptionLoaded(referral) ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} title="Loaded" aria-label="Loaded">
                                <FeatherIcon
                                  icon="check"
                                  size={16}
                                  style={{ color: '#4caf50' }}
                                />
                                <span style={{ color: '#4caf50', fontSize: '0.875rem' }}>Loaded</span>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} title="Pending" aria-label="Pending">
                                <FeatherIcon
                                  icon="clock"
                                  size={16}
                                  style={{ color: '#ff9800' }}
                                />
                                <span style={{ color: '#ff9800', fontSize: '0.875rem' }}>Pending</span>
                              </div>
                            )}
                          </td>
                          <td
                            style={{ textAlign: 'center' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {loadingRowId === referral.id ? (
                              <Spinner size="sm" color="secondary" />
                            ) : (
                              <ActionMenu
                                isLoading={false}
                                options={[
                                  {
                                    label: 'View/Edit',
                                    onClick: () => handleViewDetail(referral.id)
                                  },
                                  ...(currentUser?.role === 'admin' ? [{
                                    label: 'Delete',
                                    onClick: () => handleDelete(referral),
                                    variant: 'danger' as const
                                  }] : [])
                                ]}
                              />
                            )}
                          </td>
                        </>
                      ) : (
                        // Standard tabs row (Triaged, Approved)
                        <>
                          <td onClick={(e) => e.stopPropagation()}>
                            {isProcessing ? (
                              <Spinner size="sm" color="secondary" />
                            ) : (
                              <input
                                type="checkbox"
                                checked={selectedReferralIds.has(referral.id)}
                                onChange={(e) => handleSelectRow(referral.id, e.target.checked)}
                                disabled={bulkProcessing}
                              />
                            )}
                          </td>
                          <td>
                            {formatDate(referral.updated_at)}
                          </td>
                          <td>
                            <span 
                              className="patient-link"
                              style={{ color: '#E84B37' }}
                            >
                              {referral.patient_first_name} {referral.patient_last_name}
                            </span>
                            {referral.recommended_clinic_name && (
                              <div style={{ marginTop: '3px' }}>
                                <span style={{
                                  display: 'inline-block',
                                  padding: '1px 7px',
                                  borderRadius: '10px',
                                  fontSize: '0.72rem',
                                  background: (referral.clinic_recommendation_confidence ?? 100) >= 70 ? '#d1fae5' : (referral.clinic_recommendation_confidence ?? 100) >= 40 ? '#fef3c7' : '#fee2e2',
                                  color: (referral.clinic_recommendation_confidence ?? 100) >= 70 ? '#065f46' : (referral.clinic_recommendation_confidence ?? 100) >= 40 ? '#92400e' : '#991b1b',
                                }}>
                                  {referral.recommended_clinic_name}
                                </span>
                              </div>
                            )}
                          </td>
                          <td>
                            <SourceBadge source={referral.source} />
                          </td>
                          <td>
                            {formatDate(referral.date_of_injury)}
                          </td>
                          <td>
                            {referral.referral_type ? referral.referral_type.replace(/_/g, ' ') : 'N/A'}
                          </td>
                          <td>
                            {hasTriageData(referral) ? (
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                <TriageCategoryBadge 
                                  category={referral.triage_category_final || referral.triage_category_suggested}
                                  variant="short"
                                />
                                {referral.triage_approved && activeTab !== 'approved' && (
                                  <span style={{ color: 'green' }}>✓ Approved</span>
                                )}
                              </div>
                            ) : activeTab === 'triaged' ? (
                              // AXIS161: Show "Pending" for pre-triage referrals in the Triaged tab
                              // These are referrals where automated extraction hasn't completed yet
                              <span style={{ color: '#999', fontStyle: 'italic' }}>Pending</span>
                            ) : (
                              <span style={{ color: '#666' }}>Not triaged</span>
                            )}
                          </td>
                          <td>
                            {referral.triage_data?.confidence_percentage !== null && referral.triage_data?.confidence_percentage !== undefined && !referral.triage_approved ? (
                              <span 
                                className={`confidence-badge ${referral.triage_data.confidence_percentage >= 70 ? 'confidence-high' : referral.triage_data.confidence_percentage >= 40 ? 'confidence-medium' : 'confidence-low'}`}
                                title={`AI confidence: ${referral.triage_data.confidence_percentage}%`}
                              >
                                {referral.triage_data.confidence_percentage}%
                              </span>
                            ) : referral.triage_approved ? (
                              <span style={{ color: '#666', fontStyle: 'italic' }}>N/A</span>
                            ) : !hasTriageData(referral) && activeTab === 'triaged' ? (
                              // AXIS161: Show "Pending" in confidence column for pre-triage referrals in Triaged tab
                              <span style={{ color: '#999', fontStyle: 'italic' }}>Pending</span>
                            ) : (
                              <span style={{ color: '#666' }}>-</span>
                            )}
                          </td>
                          {/* AXIS167: Reception status indicator column */}
                          <td>
                            {isReceptionLoaded(referral) ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} title="Loaded" aria-label="Loaded">
                                <FeatherIcon
                                  icon="check"
                                  size={16}
                                  style={{ color: '#4caf50' }}
                                />
                                <span style={{ color: '#4caf50', fontSize: '0.875rem' }}>Loaded</span>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} title="Pending" aria-label="Pending">
                                <FeatherIcon
                                  icon="clock"
                                  size={16}
                                  style={{ color: '#ff9800' }}
                                />
                                <span style={{ color: '#ff9800', fontSize: '0.875rem' }}>Pending</span>
                              </div>
                            )}
                          </td>
                          <td
                            style={{ textAlign: 'center' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {loadingRowId === referral.id ? (
                              <Spinner size="sm" color="secondary" />
                            ) : (
                              <ActionMenu
                                isLoading={extractingTriageId === referral.id || approvingTriageId === referral.id || assigningToAdminId === referral.id}
                                options={[
                                  {
                                    label: hasTriageData(referral) ? 'Reprocess' : 'Extract Triage',
                                    onClick: () => handleExtractTriage(referral.id),
                                    isLoading: extractingTriageId === referral.id,
                                    disabled: extractingTriageId === referral.id || approvingTriageId === referral.id || assigningToAdminId === referral.id
                                  },
                                  ...(hasTriageData(referral) && !referral.triage_approved ? [{
                                    label: 'Approve',
                                    onClick: () => handleApproveTriage(referral.id),
                                    isLoading: approvingTriageId === referral.id,
                                    disabled: approvingTriageId === referral.id || extractingTriageId === referral.id || assigningToAdminId === referral.id || !(referral.triage_category_final || referral.triage_category_suggested),
                                    variant: 'success' as const
                                  }] : []),
                                  // AXIS209: Assign to Admin action (visible to admin, manager, clinician)
                                  ...(currentUser?.role && ['admin', 'manager', 'clinician'].includes(currentUser.role) && !referral.triage_approved && !referral.assigned_to_admin ? [{
                                    label: 'Assign to Admin',
                                    onClick: () => handleAssignToAdmin(referral.id),
                                    isLoading: assigningToAdminId === referral.id,
                                    disabled: assigningToAdminId === referral.id || extractingTriageId === referral.id || approvingTriageId === referral.id,
                                    variant: 'warning' as const
                                  }] : []),
                                  {
                                    label: 'Delete',
                                    onClick: () => handleDelete(referral),
                                    variant: 'danger' as const,
                                    disabled: extractingTriageId === referral.id || approvingTriageId === referral.id || assigningToAdminId === referral.id
                                  }
                                ]}
                              />
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total_pages > 1 && (
            <div className="pagination" style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem' }}>
              <button 
                className="btn btn-secondary"
                disabled={!pagination.has_previous || loading}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              
              <span className="pagination-info">
                Page {currentPage} of {pagination.total_pages} ({pagination.total_count} total)
              </span>
              
              <button 
                className="btn btn-secondary"
                disabled={!pagination.has_next || loading}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>

              <select 
                value={pageSize} 
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{ marginLeft: '1rem', padding: '0.5rem' }}
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
          )}
        </div>

        {selectedReferral && (
          <TriageDetailModal
            referral={selectedReferral}
            isOpen={showDetailModal}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedReferral(null);
              fetchReferrals();
              fetchTabCounts();
            }}
            onReferralUpdate={handleReferralUpdate}
            userRole={currentUser?.role}
          />
        )}

        {/* AXIS165: Reprocess Confirmation Modal */}
        {showReprocessConfirm && pendingReprocessReferral && (
          <div
            className="modal-overlay"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={handleCancelReprocess}
          >
            <div
              className="modal-content"
              style={{
                background: 'white',
                borderRadius: '8px',
                padding: '1.5rem',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ marginBottom: '1rem' }}>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#E84B37' }}>
                  <FeatherIcon icon="alert-triangle" size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                  Confirm Reprocess
                </h3>
                <p style={{ margin: 0, color: '#666' }}>
                  This referral has manually edited triage fields. Reprocessing will overwrite these edits with new AI-extracted values.
                </p>
              </div>

              <div
                style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '4px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}
              >
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 500 }}>The following fields will be overwritten:</p>
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  {pendingReprocessReferral.overridden_triage_fields?.map((field) => (
                    <li key={field} style={{ textTransform: 'capitalize' }}>
                      {field.replace(/_/g, ' ')}
                    </li>
                  ))}
                </ul>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-secondary"
                  onClick={handleCancelReprocess}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleConfirmReprocess}
                  disabled={extractingTriageId === pendingReprocessId}
                  style={{
                    padding: '0.5rem 1rem',
                    border: 'none',
                    borderRadius: '4px',
                    background: '#E84B37',
                    color: 'white',
                    cursor: extractingTriageId === pendingReprocessId ? 'not-allowed' : 'pointer',
                    opacity: extractingTriageId === pendingReprocessId ? 0.7 : 1
                  }}
                >
                  {extractingTriageId === pendingReprocessId ? (
                    <>
                      <Spinner size="sm" color="default" style={{ marginRight: '0.5rem' }} />
                      Reprocessing...
                    </>
                  ) : (
                    'Confirm Reprocess'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {referralToDelete && (
          <ConfirmationModal
            isOpen={showDeleteConfirm}
            title="Delete Referral"
            message="Are you sure you want to delete this referral? This action cannot be undone. The referral will be permanently removed from the system."
            confirmLabel="Delete"
            onConfirm={confirmDelete}
            onCancel={() => {
              setShowDeleteConfirm(false);
              setReferralToDelete(null);
              setDeleteError(null);
            }}
            isLoading={deleting}
            variant="danger"
            details={[
              { label: 'Patient', value: `${referralToDelete.patient_first_name} ${referralToDelete.patient_last_name}` },
              { label: 'Referral ID', value: referralToDelete.id },
              { label: 'Triage Status', value: referralToDelete.triage_approved ? 'Approved' : 'Triaged' }
            ]}
            error={deleteError}
            preventCloseOnOverlayClick={deleting}
          />
        )}
      </div>
    </Layout>
  );
};

